<template>
  <div>

    <error-dialog :status="loadStatus" />
    <error-dialog :status="saveContactStatus" />
    <error-dialog :status="deleteContactStatus" />

    <dialog-scroll-lock v-if="showAddField" width="300" v-model="showAddField">
      <v-card>
        <div class="pt-3 px-3">
          <v-form v-model="isNewFieldNameValid">
            <v-text-field
              label="Field name" persistent-placeholder placeholder=""
              :rules="[ v => !!v && (fields.find(x => x.key == v) ? 'Field already exists' : true) ]"
              v-model="newFieldName"
            />
          </v-form>
        </div>
        <v-card-actions class="px-3">
          <v-spacer />
          <v-btn @click="showAddField = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="!isNewFieldNameValid"
            @click="fields.push({ key: newFieldName, type: newFieldName == 'address' ? 'address' : 'text' }); newFieldName = ''; showAddField = false;"
          >
            Add
          </v-btn>
        </v-card-actions>
      </v-card>
    </dialog-scroll-lock>

    <dialog-scroll-lock v-if="selected" width="400" :value="true" @input="selected = null">
      <v-card>
        <v-btn class="float-right ma-3" icon @click="selected = null">
          <v-icon v-html="mdiClose" />
        </v-btn>
        <v-card-title>
          <template v-if="selected.id">Contact</template>
          <template v-else>Add contact</template>
        </v-card-title>
        <v-divider />
        <div style="clear: both;" />
        <div class="pl-3 pl-sm-6 pr-2 pr-sm-3 pt-3 pb-1">
          <div v-for="field, i in fields" class="py-3">
            <div class="text--label-active">{{field.key}}</div>
            <component
              :is="{ address: 'ContactFieldAddress', date: 'ContactFieldDate', 'phone number': 'ContactFieldPhone' }[field.type] || 'VTextField'"
              class="mt-0 flex-no-wrap"
              dense hide-details
              :disabled="saveContactStatus.pending"
              :value="selected.contact[field.key]"
              @input="v => $set(selected.contact, field.key, v)"
            >
              <template v-slot:append-outer>
                <v-menu bottom offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" v-blur class="px-0" text x-small>
                      <v-icon small v-html="SEARCH_FIELD_TYPES[field.type]" />
                      <v-icon small v-html="mdiMenuDown" />
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item
                      v-for="icon, type in SEARCH_FIELD_TYPES" :key="type"
                      class="overline short"
                      :input-value="field.type == type"
                      :disabled="(field.type != type) && [ 'name', 'email', 'phone', 'birthday', 'address' ].includes(field.key)"
                      @click="field.type = type"
                    >
                      <v-icon class="mr-2" v-html="icon" />
                      {{type}}
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
            </component>
          </div>
        </div>
        <div class="d-flex pb-3 px-3">
          <v-btn class="text--label-active" style="text-transform: unset; letter-spacing: unset;" text small color="primary" @click="showAddField = true"><span >Add field…</span></v-btn>
        </div>
        <v-divider />
        <v-card-actions class="px-3">
          <v-btn
            v-if="selected.id"
            class="px-2"
            style="min-width: unset;"
            :loading="deleteContactStatus.pending"
            @click="deleteContact"
          >
            <v-icon v-html="mdiDelete" />
          </v-btn>
          <v-spacer />
          <v-btn @click="selected = null">{{selected.id && !isSelectedDirty ? 'Close' : 'Cancel'}}</v-btn>
          <v-btn
            color="primary"
            :disabled="!isSelectedDirty"
            :loading="saveContactStatus.pending"
            @click="saveContact"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </dialog-scroll-lock>

    <v-data-table
      class="overflow-visible no-header"
      must-sort mobile-breakpoint="0"
      :server-items-length="n"
      :options.sync="options"
      :footer-props="{ disableItemsPerPage: true, itemsPerPageOptions: [ options.itemsPerPage ] }"
      :headers="HEADERS"
      :items="rows"
      :loading="loadStatus.pending"
      @click:row="select"
    >
      <template v-slot:item.default="{ item }">
        <slot name="item" v-bind="{ item }">
          <div class="py-2 d-flex align-center">
            <slot name="item-prefix" v-bind="{ item }"></slot>
            <div>
              <div v-if="item.contact.name">{{getKey(item.contact)}}</div>
              <div v-else>–</div>
              <div v-if="!item.contact.email && !item.contact.phone" class="caption error--text">
                No phone number / email address
              </div>
              <slot name="item-targets" v-bind="{ item }">
                <span v-if="item.contact.email" class="target caption grey--text">{{item.contact.email}}</span>
                <span v-if="item.contact.phone" class="target caption grey--text">{{item.contact.phone | phone}}</span>
              </slot>
            </div>
          </div>
        </slot>
      </template>
    </v-data-table>

  </div>
</template>


<style>
.v-data-table.no-header .v-data-table-header th {
  height: unset;
}
</style>


<style scoped>
.target:not(:last-child):after {
  content: ', ';
}
</style>


<script>
import ContactFieldAddress from '@/components/ContactFieldAddress.vue';
import ContactFieldDate from '@/components/ContactFieldDate.vue';
import ContactFieldPhone from '@/components/ContactFieldPhone.vue';
import { VTextField } from 'vuetify/lib';
import { mdiClose, mdiDelete, mdiMenuDown, mdiFormatTextVariant, mdiCalendar, mdiPhone, mdiMapMarker } from '@mdi/js';


const DEFAULT_FIELDS = [
  { key: 'name', type: 'text' },
  { key: 'email', type: 'text' },
  { key: 'phone', type: 'phone number' },
  { key: 'birthday', type: 'date' }
];


const SEARCH_FIELD_TYPES = {
  text: mdiFormatTextVariant,
  date: mdiCalendar,
  'phone number': mdiPhone,
  address: mdiMapMarker
};


export default {
  name: 'contacts-table',

  components: {
    ContactFieldAddress,
    ContactFieldDate,
    ContactFieldPhone,
    VTextField
  },

  props: [ 'search' ],

  data() {
    return {
      selected: null,
      isSelectedDirty: false,

      types: {},
      fields: DEFAULT_FIELDS,
      showAddField: false,
      isNewFieldNameValid: true,
      newFieldName: '',

      options: {
        page: 1,
        itemsPerPage: 100
      },
      n: 0,
      rows: [],

      loadStatus: this.$track('load'),
      saveContactStatus: this.$track('saveContact'),
      deleteContactStatus: this.$track('deleteContact')
    }
  },

  created() {
    Object.assign(this, { SEARCH_FIELD_TYPES, HEADERS: [ { value: 'default', sortable: false } ], mdiClose, mdiDelete, mdiMenuDown });
  },

  methods: {
    getKey(contact) {
      return contact.name || contact.email || contact.phone || '';
    },

    select(row) {
      this.selected = _.cloneDeep(row);
    },

    async load(force=false) {
      const params = { ...this.search, page: this.options.page, items_per_page: this.options.itemsPerPage };
      if (!force && _.isEqual(this.loadedParams, params))
        return;
      const output = await this.$call('search-contacts', params);
      this.types = output.types;
      this.n = output.n;
      this.rows = output.page;
      this.loadedParams = _.cloneDeep(params);
    },

    async reload() {
      this.options = { page: 1, itemsPerPage: 100 };
      await this.load(true);
    },

    async saveContact() {
      const contact = this.selected.contact;
      if (!(contact.email || contact.phone))
        throw new VisibleError('At least one of <code>email</code> or <code>phone</code> is required');
      let action, output;
      if (this.selected.id) {
        action = 'update';
        output = await this.$call(
          'update-contact',
          { contact_id: this.selected.id, contact, groups: this.selected.groups }
        );
      } else {
        action = 'create';
        output = await this.$call('create-contact', contact);
      }
      if (output?.err) {
        const err = { DUPLICATE_EMAIL: 'email address', DUPLICATE_PHONE: 'phone number' }[output.err];
        throw new VisibleError('A contact already exists with this ' + err);
      }
      this.selected = null;
      this.load(true);
      this.$emit(action, output, contact);
    },

    async deleteContact() {
      const r = await this.$confirm({ title: 'Are you sure?', msg: 'Are you sure you want to delete this contact?' });
      if (r == 'No')
        return;
      await this.$call('delete-contact', { id: this.selected.id });
      this.selected = null;
      this.load(true);
    }
  },

  watch: {
    search: {
      immediate: true,
      handler() {
        this.load();
      }
    },

    options: {
      deep: true,
      handler() {
        this.load();
      }
    },

    selected: {
      deep: true,
      handler(cur, prev) {
        this.isSelectedDirty = !!cur && !!prev;
      }
    },

    types: {
      immediate: true,
      handler(v) {
        for (const [ key, type ] of Object.entries(this.types))
          if (!this.fields.find(x => x.key == key))
            this.fields.push({ key, text: key, type });
        this.$emit('update:types', v);
      }
    },

    'loadStatus.pending': {
      immediate: true,
      handler(v) {
        this.$emit('update:loading', v);
      }
    },

    n: {
      immediate: true,
      handler(v) {
        this.$emit('update:n', v);
      }
    },

    rows: {
      immediate: true,
      handler(v) {
        this.$emit('update:rows', v);
      }
    }
  }
}
</script>
